import { useState } from "react";
import {
  Order,
  OrderStatus,
  paymentStatusStyles,
  statusStyles,
} from "../../types/orders";
import { Badge } from "src/components/ui/Badge";
import { IMAGE_CDN } from "src/redux/api/appConst";
import { useUpdateOrderStatus } from "../../hooks/orders/useUpdateOrderStatus";
import { generateOrderQR } from "src/utils/generateOrderQR";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { InvoicePDF } from "./InvoicePDF";
import { toast } from "react-toastify";

const OrderCard = ({ order }: { order: Order }) => {
  const { mutate: updateStatus, isPending } = useUpdateOrderStatus();

  const [expanded, setExpanded] = useState(false);
  const [orderStatus, setOrderStatus] = useState<OrderStatus>(order.status);
  const [invoiceData, setInvoiceData] = useState<
    (Order & { qr_code?: string }) | null
  >(null);

  const previewItems = order.order_items?.slice(0, 3);
  const remainingCount = order.order_items?.length - 3;

  const handleOrderStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as OrderStatus;

    setOrderStatus(newStatus); // ✅ immediate UI update

    updateStatus({
      orderId: order.order_id,
      status: newStatus,
    });
  };

  const handleGenerateInvoice = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const qrCode = await generateOrderQR(order.order_id);

    setInvoiceData({
      ...order,
      qr_code: qrCode,
    });
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 hover:border-emerald-200 transition-all">
      {/* COLLAPSED HEADER */}
      <div
        onClick={() => setExpanded(!expanded)}
        className="p-5 cursor-pointer flex flex-col lg:flex-row lg:items-center gap-6"
      >
        {/* Customer */}
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600">
            {order.address.name[0]}
          </div>

          <div>
            <p className="font-bold text-slate-800">{order.address.name}</p>

            <Badge className={`${statusStyles[orderStatus]} rounded-full py-0`}>
              {orderStatus}
            </Badge>

            <p className="text-xs text-slate-400 font-semibold">
              #{order.order_number}
            </p>
          </div>
        </div>

        {/* Stacked Product Preview */}
        <div className="flex items-center flex-1 min-w-[200px]">
          <div className="flex -space-x-3">
            {previewItems?.map((item) => (
              <img
                key={item.id}
                src={`${IMAGE_CDN}/${item.thumbnail}`}
                alt={item.productName}
                className="w-10 h-10 rounded-lg border-2 border-white object-cover shadow-sm"
              />
            ))}
          </div>

          {remainingCount > 0 && (
            <span className="ml-3 text-xs font-semibold text-slate-500">
              +{remainingCount} more
            </span>
          )}
        </div>

        {/* Order Total */}
        <div className="flex-1 min-w-[120px]">
          <p className="text-xs text-slate-400 font-bold uppercase">Total</p>

          <p className="text-lg font-black text-emerald-600">
            ₹{order.total_amount}
          </p>
        </div>

        {/* Status */}
        <div className="flex-1 min-w-[160px]">
          <select
            value={orderStatus}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => handleOrderStatusChange(e)}
            disabled={isPending}
            className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold uppercase tracking-widest w-full"
          >
            {[
              "CREATED",
              "PENDING",
              "PLACED",
              "SHIPPED",
              "DELIVERED",
              "CANCELLED",
            ].map((status) => (
              <option key={status}>{status}</option>
            ))}
          </select>
        </div>

        {/* Expand Arrow */}
        <div className="text-slate-400 text-lg">{expanded ? "▲" : "▼"}</div>
      </div>

      {/* EXPANDED DETAILS */}
      {expanded && (
        <div className="border-t border-slate-100 px-6 py-5 space-y-6 animate-in fade-in">
          {/* Shipping */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
              Shipping Address
            </p>

            <p className="text-sm text-slate-700">
              {order.address.addressLine}, {order.address.city},{" "}
              {order.address.state} {order.address.postalCode}
            </p>

            <p className="text-xs text-slate-500 mt-1">
              📱 {order.address.phone}
            </p>
          </div>

          {/* Order Items */}
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
              Products
            </p>

            <div className="space-y-3">
              {order.order_items?.map((item) => (
                <div key={item.id} className="flex items-center gap-4">
                  <img
                    src={`${IMAGE_CDN}/${item.thumbnail}`}
                    alt={item.productName}
                    className="w-14 h-14 rounded-lg border object-cover"
                  />

                  <div className="flex-1">
                    <p className="font-semibold text-slate-800">
                      {item.productName}
                    </p>

                    <p className="text-xs text-slate-500">
                      Qty: {item.quantity}
                    </p>
                  </div>

                  <p className="font-bold text-emerald-600">
                    ₹{item.totalPrice}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Order Meta */}
          <div className="flex flex-wrap justify-between gap-4 pt-3 border-t border-slate-100">
            <p className="text-xs text-slate-500 font-semibold">
              Ordered on{" "}
              {new Date(order.created_at).toLocaleDateString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </p>
            <div className="flex gap-8 items-center">
              <div onClick={(e) => e.stopPropagation()}>
                {!invoiceData ? (
                  <button
                    onClick={handleGenerateInvoice}
                    className="text-sm hover:text-emerald-500"
                  >
                    Download Invoice
                  </button>
                ) : (
                  <PDFDownloadLink
                    document={<InvoicePDF order={invoiceData} />}
                    fileName={`invoice-${order.order_number}.pdf`}
                  >
                    {({ loading }) => (
                      <button className="text-sm hover:text-emerald-500">
                        {loading ? "Generating PDF..." : "Click To Download"}
                      </button>
                    )}
                  </PDFDownloadLink>
                )}
              </div>
              <button
                className="text-sm hover:text-emerald-500 transition-colors"
                onClick={async (e) => {
                  e.stopPropagation();
                  const addressText = `${order.address.name}, ${order.address.addressLine}, ${order.address.city}, ${order.address.state} ${order.address.postalCode} | ${order.address.phone}`;
                  await navigator.clipboard.writeText(addressText);
                  toast.success("Address copied to clipboard");
                }}
              >
                Copy Address
              </button>
            </div>
            <div>
              <p className="text-xs text-slate-500 font-semibold">
                Payment Status:{" "}
                <Badge
                  className={`${paymentStatusStyles[order.payment_status]} rounded-full text-xs py-0`}
                >
                  {order.payment_status}
                </Badge>
              </p>

              <p className="text-xs text-slate-500 font-semibold">
                Payment: {order.transaction_id}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCard;
